import { ImageGravity } from '@shared/components/image/models';

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

export const isGravity = (v: unknown): v is ImageGravity =>
  isRecord(v) &&
  typeof v['width'] === 'number' &&
  typeof v['height'] === 'number' &&
  typeof v['x'] === 'number' &&
  typeof v['y'] === 'number';

export interface HasGravityOptions {
  validateShape?: boolean;
}

/**
 * Determines whether a given input contains a property named `gravity`
 * and optionally validates its shape.
 *
 * This function recursively traverses through objects, arrays, Maps,
 * and Sets to look for a `gravity` property.
 * If `validateShape` is enabled, it further checks if the `gravity`
 * property matches a specific shape definition.
 *
 * @param {unknown} input - The input to check for the presence of a `gravity` property.
 * @param {HasGravityOptions} [options={}] - Optional settings to control validation.
 * @param {boolean} [options.validateShape=false] - When set to `true`, validates the shape of the `gravity` property.
 * @returns {boolean} Returns `true` if a `gravity` property is found (and valid if `validateShape` is enabled), otherwise `false`.
 *
 * @example
 * // Example 1: Basic usage
 * const objectWithGravity = { gravity: 9.8 };
 * const result = hasGravity(objectWithGravity);
 * console.log(result); // Outputs: true
 *
 * @example
 * // Example 2: With recursive structures
 * const nestedObject = { data: { gravity: 9.8 } };
 * const result = hasGravity(nestedObject);
 * console.log(result); // Outputs: true
 *
 * @example
 * // Example 3: Validating gravity shape
 * const incorrectShape = { gravity: "invalid" };
 * const options = { validateShape: true };
 * const result = hasGravity(incorrectShape, options);
 * console.log(result); // Outputs: false
 */
export function hasGravity(input: unknown, options: HasGravityOptions = {}): boolean {
  const { validateShape = false } = options;

  const visited = new WeakSet<object>();
  const stack: unknown[] = [input];

  while (stack.length) {
    const current = stack.pop();
    if (!isRecord(current)) continue;
    if (visited.has(current)) continue;
    visited.add(current);

    if ('gravity' in current) {
      const g = current['gravity'];
      if (!validateShape) return true;
      if (isGravity(g)) return true;
    }

    for (const value of Object.values(current)) {
      if (Array.isArray(value)) {
        for (const item of value) stack.push(item);
      } else if (isRecord(value)) {
        stack.push(value);
      } else if (value instanceof Map) {
        for (const v of value.values()) stack.push(v);
      } else if (value instanceof Set) {
        for (const v of value.values()) stack.push(v);
      }
    }
  }
  return false;
}
